"use client"

import { ColumnDef } from "@tanstack/react-table"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"

import { ArrowUpDown } from "lucide-react"
import Actions from "./Actions"
import { Periode } from "@/types"
import { formatDateToIndonesian } from "@/lib/utils"
import { Badge } from "@/components/ui/badge"

export const columns: ColumnDef<Periode>[] = [
  {
    id: "select",
    header: ({ table }) => (
      <Checkbox
        checked={
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() && "indeterminate")
        }
        onCheckedChange={(value) => table.toggleAllPageRowsSelected(!!value)}
        aria-label="Select all"
      />
    ),
    cell: ({ row }) => (
      <Checkbox
        checked={row.getIsSelected()}
        onCheckedChange={(value) => row.toggleSelected(!!value)}
        aria-label="Select row"
      />
    ),
    enableSorting: false,
    enableHiding: false,
  },
  {
    id: "actions",
    cell: ({ row }) => {
      return <Actions row={row.original} />
    },
  },
  {
    accessorKey: "tahun",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Tahun
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
  },
  {
    accessorKey: "nama_jalur_jenis",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Jalur Jenis
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
  },
  {
    accessorKey: "gelombang",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Gelombang
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return <div className="text-center">{row.getValue("gelombang")}</div>
    },
  },
  {
    accessorKey: "tahun_ajaran_masuk",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Tahun Ajaran Masuk
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return (
        <div className="text-center">{row.getValue("tahun_ajaran_masuk")}</div>
      )
    },
  },
  {
    accessorKey: "semester_masuk",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Semester Masuk
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return <div className="text-center">{row.getValue("semester_masuk")}</div>
    },
  },
  {
    accessorKey: "waktu_mulai_daftar",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Mulai Daftar
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(new Date(row.original.waktu_mulai_daftar))
    },
  },
  {
    accessorKey: "waktu_selesai_daftar",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Daftar
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(new Date(row.original.waktu_selesai_daftar))
    },
  },
  {
    accessorKey: "waktu_selesai_daftar_valid",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Validasi Pendaftaran
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      if (!row.original.waktu_selesai_daftar_valid) return "-"
      return formatDateToIndonesian(
        new Date(row.original.waktu_selesai_daftar_valid)
      )
    },
  },
  {
    accessorKey: "waktu_pengumuman_kelulusan",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Pengumuman Kelulusan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      if (!row.original?.waktu_pengumuman_kelulusan) return null
      return formatDateToIndonesian(
        new Date(row.original?.waktu_pengumuman_kelulusan)
      )
    },
  },
  {
    accessorKey: "waktu_mulai_isi_ukt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Mulai Isi UKT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(new Date(row.original.waktu_mulai_isi_ukt))
    },
  },
  {
    accessorKey: "waktu_selesai_isi_ukt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Isi UKT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_selesai_isi_ukt)
      )
    },
  },
  {
    accessorKey: "waktu_pengumuman",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Pengumuman
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(new Date(row.original.waktu_pengumuman))
    },
  },
  {
    accessorKey: "waktu_mulai_bayar_ukt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Mulai Bayar UKT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return (
        row.original.waktu_mulai_bayar_ukt &&
        formatDateToIndonesian(new Date(row.original.waktu_mulai_bayar_ukt))
      )
    },
  },
  {
    accessorKey: "waktu_selesai_bayar_ukt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Bayar UKT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return (
        row.original.waktu_selesai_bayar_ukt &&
        formatDateToIndonesian(new Date(row.original.waktu_selesai_bayar_ukt))
      )
    },
  },
  {
    accessorKey: "waktu_mulai_daftarkembali",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Mulai Daftar Kembali
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_mulai_daftarkembali)
      )
    },
  },
  {
    accessorKey: "waktu_selesai_daftarkembali",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Daftar Kembali
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_selesai_daftarkembali)
      )
    },
  },
  {
    accessorKey: "waktu_mulai_sanggah_berkas_daftarkembali",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Mulai Sanggah Berkas Daftar Kembali
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_mulai_sanggah_berkas_daftarkembali)
      )
    },
  },
  {
    accessorKey: "waktu_selesai_sanggah_berkas_daftarkembali",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Sanggah Berkas Daftar Kembali
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_selesai_sanggah_berkas_daftarkembali)
      )
    },
  },
  {
    accessorKey: "waktu_mulai_sanggah_kelulusan",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Mulai Sanggah Kelulusan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_mulai_sanggah_kelulusan)
      )
    },
  },
  {
    accessorKey: "waktu_selesai_sanggah_kelulusan",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Sanggah Kelulusan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_selesai_sanggah_kelulusan)
      )
    },
  },
  {
    accessorKey: "waktu_pengumuman_sanggah_kelulusan",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Pengumuman Sanggah Kelulusan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_pengumuman_sanggah_kelulusan)
      )
    },
  },
  {
    accessorKey: "waktu_pengumuman_kelulusan_cadangan",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Pengumuman Kelulusan Cadangan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_pengumuman_kelulusan_cadangan)
      )
    },
  },
  {
    accessorKey: "waktu_mulai_daftarkembali_cadangan",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Mulai Daftar Kembali Cadangan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_mulai_daftarkembali_cadangan)
      )
    },
  },
  {
    accessorKey: "waktu_selesai_daftarkembali_cadangan",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Waktu Selesai Daftar Kembali Cadangan
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(
        new Date(row.original.waktu_selesai_daftarkembali_cadangan)
      )
    },
  },
  {
    accessorKey: "tanggal_mulai_kuliah",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Tanggal Mulai Kuliah
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return formatDateToIndonesian(new Date(row.original.tanggal_mulai_kuliah))
    },
  },
  {
    accessorKey: "link_telegram",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Link Telegram
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
  },
  {
    accessorKey: "link_telegram_pkkmb",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Link Telegram PKKMB
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
  },
  {
    accessorKey: "tanggal_cbt",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Tanggal CBT
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return <div>{row.original.tanggal_cbt?.join(", ")}</div>
    },
  },
  {
    accessorKey: "is_cek_jurusan_sekolah",
    header: ({ column }) => {
      return (
        <Button
          variant="ghost"
          onClick={() => column.toggleSorting(column.getIsSorted() === "asc")}
        >
          Cek Jurusan Sekolah
          <ArrowUpDown className="w-4 h-4 ml-2" />
        </Button>
      )
    },
    cell: ({ row }) => {
      return row.getValue("is_cek_jurusan_sekolah") ? (
        <Badge>Ya</Badge>
      ) : (
        <Badge variant={"secondary"}>Tidak</Badge>
      )
    },
  },
]
